import React, { useState, useMemo, useEffect } from "react";
import {
  AzureMap,
  AzureMapDataSourceProvider,
  AzureMapFeature,
  AzureMapLayerProvider,
  AzureMapPopup,
  IAzureMapControls,
  IAzureMapFeature,
  IAzureMapOptions,
  useAzureMaps,
} from "react-azure-maps";
import {
  AuthenticationType,
  data,
  ControlPosition,
  AuthenticationOptions,
} from "azure-maps-control";
import atlas from "azure-maps-control";
import AzureLocationService, {
  RawZone,
  FloorplanResponse,
} from "../services/azure-location-service";
import { useMqttBroker } from "../hooks/use-mqttBroker";
import { MqttConnectionData, MqttGeoLocationPoint } from "../types";
import { extractErrorMessage } from "../unit/utils";
import { LocationResponse } from "../hooks/use-signalR";
import * as Labels from "../unit/labels";
import { makeStyles, Tooltip, IconButton } from "@material-ui/core";
import CarIcon from "@material-ui/icons/DirectionsCar";
import DirectionsIcon from "@material-ui/icons/Directions";
import { buildComparisonConfig, createDaysList } from "../helpers";
import { RouteDurationSlider } from "./RouteDurationSlider";

const useStyles = makeStyles({
  btns_container: {
    display: "flex",
    height: "100%",
    width: "100%",
    margin: "10px 10px",
    justifyContent: "flex-between",
  },
  car_icon: {
    color: "#46a4eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ededeb",
    border: "1px solid #46a4eb",
    borderRadius: "10px",
    margin: "3px",
    width: "46px",
    height: "46px",
    "&:hover": {
      backgroundColor: "#e6f3fc",
    },
  },
  directions_button: {
    backgroundColor: "#2a7fbd",
    color: "white",
    border: "1px solid #2a7fbd",
    borderRadius: "10px",
    margin: "3px",
    width: "46px",
    height: "46px",
    "&:hover": {
      backgroundColor: "#1e5f8f",
    },
    "&:disabled": {
      backgroundColor: "#cccccc",
      color: "#666666",
    },
  },
});

interface LocationProps {
  vehicleId: string | null;
  errorObserver: (message: string) => void;
  mqttConfig: MqttConnectionData | null;
  liberkeeConfig: LocationResponse | null;
}

export interface CoordsResponse {
  x: number;
  y: number;
  position?: GeolocationPosition | null;
  id?: string;
}

interface TrafficOptions {
  incidents?: boolean;
  flow: "relative" | "none" | "absolute" | "relative-delay" | undefined;
}

interface MarkersView {
  vehicleLocation: IAzureMapFeature | null;
  circleArea: IAzureMapFeature | null;
  zones: IAzureMapFeature[];
}

interface RenderData {
  id: string;
  position?: { longitude: number; latitude: number };
  positions?: Array<[number, number]>;
  geoLocation?: GeolocationPosition | null;
  radius?: number;
}

const inferVehicleRelevantCoords = (
  initialConfig: MqttConnectionData | LocationResponse | null
): CoordsResponse => {
  const initialCoords = { x: 0, y: 0 };
  if (!initialConfig) {
    return initialCoords;
  }
  if ((initialConfig as MqttConnectionData)?.geolocation?.length) {
    const { latitude, longitude } = (initialConfig as MqttConnectionData)!
      .geolocation![0];
    return { x: latitude, y: longitude };
  }

  const liberkeeLat = (initialConfig as LocationResponse).liberkeeLat;
  const liberkeeLon = (initialConfig as LocationResponse).liberkiLon;
  if (!!liberkeeLat && !!liberkeeLon) {
    return { x: liberkeeLat, y: liberkeeLon };
  }

  return initialCoords;
};

const inferDeviationValue = (
  mqttConfig: MqttConnectionData | null
): number | null => {
  if (!mqttConfig) {
    return null;
  }

  if (mqttConfig.geolocation?.length) {
    const { margin } = mqttConfig.geolocation[0];
    return margin;
  }

  return null;
};

const getGeolocationPropertyName = (
  mqttData: MqttConnectionData | null,
  property: "zoneName" | "siteName"
) => {
  if (!mqttData) {
    return null;
  }
  const { type, geolocation } = mqttData;
  if (type === "asset" && geolocation) {
    const key = property as keyof MqttGeoLocationPoint;
    return geolocation[0][key] as any;
  }
  return null;
};

export interface RouteDurationHistory {
  lengthInDays: number;
  monthsIntersect: boolean;
  comparisonConfig: ComparisonConfig | null;
  dividingMonthsIndex: number;
  max: number;
  min: number;
  range: number[];
}

interface ComparisonConfig {
  [key: string]: number;
}

export const CarLocation: React.FunctionComponent<LocationProps> = (
  props: LocationProps
): React.ReactElement => {
  const setHistoryRange = (
    mqttData: MqttConnectionData | null
  ): RouteDurationHistory | null => {
    if (!mqttData?.geolocation || !mqttData.history) return null;

    const { geolocation } = mqttData;
    const start = new Date(geolocation[0].time);
    const end = new Date(geolocation[geolocation.length - 1].time);
    const startMonth = start.getMonth();
    const endMonth = end.getMonth();

    let currentDays: number[] = [];
    let prevDays: number[] = [];
    let lengthInDays = 0;
    const monthsIntersect = startMonth !== endMonth;
    let max = 0;
    let min = 0;
    let dividingMonthsIndex = 0;
    let comparisonConfig: ComparisonConfig | null = null;

    if (monthsIntersect) {
      dividingMonthsIndex = geolocation.findIndex(
        (p) => new Date(p.time).getMonth() === endMonth
      );
      if (dividingMonthsIndex > -1) {
        const prevMax = new Date(
          geolocation[dividingMonthsIndex].time
        ).getDate();
        const prevMin = end.getDate();
        prevDays = createDaysList(prevMax, prevMin);
        lengthInDays += prevMax - prevMin + 1;

        const currMax = start.getDate();
        const currMin = new Date(
          geolocation[dividingMonthsIndex - 1].time
        ).getDate();
        currentDays = createDaysList(currMax, currMin);
        lengthInDays += currMax - currMin + 1;

        const cmp = buildComparisonConfig(currentDays, Math.max(...prevDays));
        comparisonConfig = cmp.comparisonConfig;
        max = cmp.max;
        min = Math.min(...prevDays);
      }
    } else {
      max = start.getDate();
      min = end.getDate();
      currentDays = createDaysList(max, min);
      lengthInDays += max - min + 1;
    }

    const range = [...currentDays, ...prevDays];
    return {
      lengthInDays,
      monthsIntersect,
      comparisonConfig,
      dividingMonthsIndex,
      max,
      min,
      range,
    };
  };

  const [key] = useState<string>("nLjj-AX5hsSoulCa6A9l8PkNTeSUY-XPJdVZT7WpxR4");
  const [popupOptions, setPopupOptions] = useState({});
  const [readyForUser, setReadyForUser] = useState<boolean>(false);
  const [loading, setLodaing] = useState<boolean>(false);
  const [zones, setZones] = useState<RawZone[]>([]);
  const [floorplan, setFloorplan] = useState<FloorplanResponse | null>(null);
  const { vehicleId, errorObserver, mqttConfig, liberkeeConfig } = props;
  const routeHistory = useMemo(() => setHistoryRange(mqttConfig), [mqttConfig]);
  const [vehicleCoords, setVehicleCoords] = useState<CoordsResponse>(
    inferVehicleRelevantCoords(mqttConfig ?? liberkeeConfig)
  );
  const [zoneName, setZoneName] = useState<string | null>(
    getGeolocationPropertyName(mqttConfig, "zoneName")
  );
  const [siteName, setSiteName] = useState<string | null>(
    getGeolocationPropertyName(mqttConfig, "siteName")
  );
  const [deviationValue, setDeviationValue] = useState<number | null>(
    inferDeviationValue(mqttConfig)
  );

  const { mqttLat, mqttLon, deviation, site, zone } = useMqttBroker(
    !!mqttConfig ? `wss://${mqttConfig.mqttConfig!.url}/mqtt` : "",
    mqttConfig
  );

  const { mapRef, isMapReady } = useAzureMaps();
  const classes = useStyles();
  const ZOOM = 17;

  const renderPoint = (data: RenderData): IAzureMapFeature => {
    return (
      <AzureMapFeature
        key={data.id}
        id={data.id}
        type="Point"
        variant="shape"
        coordinate={
          data?.position
            ? [data.position.longitude, data.position.latitude]
            : undefined
        }
        properties={{
          id: data.id,
        }}
        setCoords={
          data?.position
            ? [data.position.longitude, data.position.latitude]
            : undefined
        }
        setProperties={data?.geoLocation ? data.geoLocation : undefined}
      />
    );
  };

  const renderCircle = (data: RenderData): IAzureMapFeature => {
    return (
      <AzureMapFeature
        key={data.id}
        id={data.id}
        type="Point"
        variant="shape"
        coordinate={
          data?.position
            ? [data.position.longitude, data.position.latitude]
            : undefined
        }
        properties={{
          id: data.id,
          subType: "Circle",
          radius: data.radius, //meters
          //popUpProp: data,
        }}
        setCoords={
          data?.position
            ? [data.position.longitude, data.position.latitude]
            : undefined
        }
        setProperties={data?.geoLocation ? data.geoLocation : undefined}
      />
    );
  };

  const isValidCoordinate = (coord: number): boolean => {
    return typeof coord === 'number' && !isNaN(coord) && isFinite(coord) && coord !== 0;
  };

  const setCamera = (y: number, x: number) => {
    if (isValidCoordinate(y) && isValidCoordinate(x)) {
      mapRef!.setCamera({ center: [y, x] });
    } else {
      console.warn("Invalid coordinates for camera:", { y, x });
    }
  };

  useEffect(() => {
    if (!!mqttLat && !!mqttLon) {
      setVehicleCoords((prevCoords) => ({
        ...prevCoords,
        x: mqttLat,
        y: mqttLon,
      }));
    }
  }, [mqttLat, mqttLon]);

  useEffect(() => {
    if (deviation) {
      setDeviationValue(deviation);
    }
  }, [deviation]);

  useEffect(() => {
    if (isMapReady && mapRef) {
      const hasValidCoords = !!(
        vehicleCoords.x &&
        vehicleCoords.y &&
        !isNaN(vehicleCoords.x) &&
        !isNaN(vehicleCoords.y)
      );
      const hasMqttCoords = !!(
        mqttConfig?.geolocation?.[0]?.latitude &&
        mqttConfig?.geolocation?.[0]?.longitude &&
        !isNaN(mqttConfig.geolocation[0].latitude) &&
        !isNaN(mqttConfig.geolocation[0].longitude)
      );

      if (hasValidCoords) {
        setCamera(vehicleCoords.y, vehicleCoords.x);
      } else if (hasMqttCoords && mqttConfig?.geolocation) {
        const { latitude, longitude } = mqttConfig.geolocation[0];
        setCamera(longitude, latitude);
      } else {
        console.log("No valid coordinates available for centering");
      }
    } else {
      console.log("Map not ready");
    }
  }, [
    vehicleCoords.x,
    vehicleCoords.y,
    mqttConfig?.geolocation,
    isMapReady,
    mapRef,
  ]);

  const trafficOptions: TrafficOptions = useMemo(() => {
    return {
      flow: "none",
    };
  }, []);

  const styleOptions = {
    style: "satellite_road_labels",
    renderWorldCopies: true,
    showBuildingModels: true,
  };

  const option: AuthenticationOptions = useMemo(() => {
    return {
      authOptions: {
        authType: AuthenticationType.subscriptionKey,
        subscriptionKey: key,
      },
      zoom: ZOOM,
      view: "Auto",
      showLabels: true,
    };
  }, []);

  const controls: IAzureMapControls[] = [
    {
      controlName: "CompassControl",
      options: {
        position: ControlPosition.TopLeft,
      },
    },
    {
      controlName: "ZoomControl",
      options: {
        position: ControlPosition.BottomRight,
      },
    },
    {
      controlName: "TrafficControl",
      options: {
        position: ControlPosition.TopRight,
      },
    },
    {
      controlName: "TrafficLegendControl",
      options: {
        position: ControlPosition.BottomLeft,
      },
    },
    {
      controlName: "PitchControl",
      options: {
        position: ControlPosition.TopRight,
      },
    },
  ];

  const getVehicleLocation = async () => {
    setLodaing(true);
    try {
      if (!!mqttConfig && vehicleId) {
        const { geolocation } =
          await AzureLocationService.determineMqttPresence(vehicleId);
        if (geolocation) {
          const { latitude, longitude, margin } = geolocation[0];
          if (latitude && longitude && margin) {
            setVehicleCoords((prevCoords) => ({
              ...prevCoords,
              x: latitude,
              y: longitude,
            }));
            setDeviationValue(margin);
            setCamera(longitude, latitude);
          }
        }
      }
      setLodaing(false);
    } catch (error: any) {
      console.log(error);
      setLodaing(false);
      const { errorMessage } = extractErrorMessage(error);
      errorObserver(errorMessage || Labels.CoordinatesError);
    }
  };

  const onGetDirectionsClick = () => {
    if (vehicleId) {
      AzureLocationService.triggerPosition(vehicleId)
        .then(() => {})
        .catch(() => {});

      const lat = vehicleCoords.x;
      const lng = vehicleCoords.y;
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isInIframe = window.self !== window.top;

      if (isInIframe) {
        try {
          if (isIOS) {
            window.parent.open(
              `http://maps.apple.com/?q=${lat},${lng}`,
              "_blank"
            );
          } else if (isAndroid) {
            window.parent.open(
              `https://maps.google.com/?q=${lat},${lng}`,
              "_blank"
            );
          } else {
            window.parent.open(
              `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
              "_blank"
            );
          }
        } catch (error) {
          console.log(`📍 Coordinates: ${lat}, ${lng}`);
          alert(
            `Coordinates: ${lat}, ${lng}\nCopy these coordinates to use in your maps app.`
          );
        }
      } else {
        if (isIOS) {
          window.location.href = `http://maps.apple.com/?q=${lat},${lng}`;
        } else if (isAndroid) {
          window.location.href = `https://maps.google.com/?q=${lat},${lng}`;
        } else {
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
            "_blank"
          );
        }
      }
    }
  };

  const markers: MarkersView = useMemo(() => {
    const availableVehicleCoords = isValidCoordinate(vehicleCoords.x) && isValidCoordinate(vehicleCoords.y);

    return {
      vehicleLocation: availableVehicleCoords
        ? renderPoint({
            id: "Vehicle",
            position: { longitude: vehicleCoords.y, latitude: vehicleCoords.x },
            geoLocation: null,
          })
        : null,
      circleArea:
        typeof deviationValue === "number" && !!mqttConfig
          ? renderCircle({
              id: "Circle Area",
              position: {
                longitude: vehicleCoords.y,
                latitude: vehicleCoords.x,
              },
              radius: deviationValue,
            })
          : null,
      zones: zones.map((zone) => {
        let coordinates: [number, number][] = [];

        if (
          zone.geometry.type === "Polygon" &&
          zone.geometry.coordinates?.[0]
        ) {
          coordinates = zone.geometry.coordinates[0].map(
            (coord: any) => [coord[0], coord[1]] as [number, number]
          );
        } else if (
          zone.geometry.type === "MultiPolygon" &&
          zone.geometry.coordinates[0]?.[0]
        ) {
          coordinates = zone.geometry.coordinates[0][0].map(
            (coord: any) => [coord[0], coord[1]] as [number, number]
          );
        }

        return (
          <AzureMapFeature
            key={`zone-${zone.id}`}
            id={`zone-${zone.id}`}
            type="Polygon"
            variant="shape"
            coordinates={coordinates}
            properties={{
              name: zone.name,
              color: zone.color,
              siteId: zone.site_id,
            }}
          />
        );
      }),
    };
  }, [vehicleCoords, deviationValue, mqttConfig, zones]);

  const propertiesAccess = mqttConfig?.mqtt && mqttConfig?.type === "asset";

  useEffect(() => {
    if (propertiesAccess) {
      if (typeof zone === "string" && typeof site === "string") {
        setZoneName(zone);
        setSiteName(site);
      }
    }
  }, [propertiesAccess, site, zone]);

  useEffect(() => {
    const fetchZonesAndFloorplan = async () => {
      try {
        const siteId = mqttConfig?.siteId;

        if (siteId) {
          const [zonesData, floorplanData] = await Promise.all([
            AzureLocationService.getZones(siteId),
            AzureLocationService.getFloorplan(siteId),
          ]);
          setZones(zonesData);
          setFloorplan(floorplanData);
        }
      } catch (error) {
        console.error("Failed to fetch zones or floorplan:", error);
      }
    };

    if (isMapReady && mqttConfig) {
      fetchZonesAndFloorplan();
    }
  }, [isMapReady, mqttConfig]);

  const rightBar: React.ReactElement = useMemo(() => {
    return (
      <div
        style={{
          visibility: propertiesAccess ? "visible" : "hidden",
          display: "flex",
          gap: "20px",
          fontSize: "14px",
          color: "#2a7fbd",
        }}
      >
        <div>
          Site:{" "}
          <span style={{ color: "#848687" }}>
            {!!siteName ? siteName : Labels.KEINE_DATEN}
          </span>
        </div>
        <div>
          Zone:{" "}
          <span style={{ color: "#848687" }}>
            {!!zoneName ? zoneName : Labels.KEINE_DATEN}
          </span>
        </div>
      </div>
    );
  }, [propertiesAccess, zoneName, siteName]);

  useEffect(() => {
    if (isMapReady && mapRef && floorplan) {
      try {
        const { overlayBox, overlayFile } = floorplan;
        const imageBounds = [
          [overlayBox.long2, overlayBox.lat1],
          [overlayBox.long1, overlayBox.lat1],
          [overlayBox.long1, overlayBox.lat2],
          [overlayBox.long2, overlayBox.lat2],
        ] as atlas.data.Position[];
        const existingLayer = mapRef.layers.getLayerById('floorplan-layer');
        if (existingLayer) {
          mapRef.layers.remove('floorplan-layer');
        }

        const imageLayer = new atlas.layer.ImageLayer({
          url: overlayFile,
          coordinates: imageBounds,
          opacity: 0.8,
        });
        
        try {
          mapRef.layers.add(imageLayer);
          
          setTimeout(() => {
            mapRef.getCamera();
          }, 200);
        } catch (error) {
          console.error('Error adding floorplan layer:', error);
        }
      } catch (error) {
        console.error("Failed to add floorplan layer:", error);
      }
    }
  }, [isMapReady, mapRef, floorplan, mqttConfig?.siteId]);

  useEffect(() => {
    if (isMapReady && mapRef && floorplan && zones.length > 0) {
      setTimeout(() => {
        const zonesLayer = mapRef.layers.getLayerById('zones-fill-layer');
        const zonesBorderLayer = mapRef.layers.getLayerById('zones-border-layer');
        const vehicleLayer = mapRef.layers.getLayerById(`MultiplePoint AzureMapLayerProvider Vehicle`);
        const circleLayer = mapRef.layers.getLayerById(`SinglePoint AzureMapLayerProvider Circle`);
        
        if (zonesLayer) {
          mapRef.layers.remove(zonesLayer);
          mapRef.layers.add(zonesLayer);
        }
        if (zonesBorderLayer) {
          mapRef.layers.remove(zonesBorderLayer);
          mapRef.layers.add(zonesBorderLayer);
        }
        if (circleLayer) {
          mapRef.layers.remove(circleLayer);
          mapRef.layers.add(circleLayer);
        }
        if (vehicleLayer) {
          mapRef.layers.remove(vehicleLayer);
          mapRef.layers.add(vehicleLayer);
        }
        
        console.log('Zones and markers re-added on top of floorplan');
      }, 300);
    }
  }, [isMapReady, mapRef, floorplan, zones, markers.vehicleLocation, markers.circleArea]);

  return (
    <div>
      <div style={{ margin: "10px 10px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "10px",
          }}
        >
          {Boolean(vehicleCoords.y) && Boolean(vehicleCoords.x) && (
            <Tooltip
              placement="bottom"
              title="Standort des Fahrzeugs"
              onClick={getVehicleLocation}
            >
              <IconButton disabled={loading} className={classes.car_icon}>
                <CarIcon />
              </IconButton>
            </Tooltip>
          )}
          {Boolean(vehicleCoords.y) && Boolean(vehicleCoords.x) && (
            <Tooltip
              placement="bottom"
              title="Get directions to vehicle location"
            >
              <IconButton
                onClick={onGetDirectionsClick}
                disabled={loading}
                className={classes.directions_button}
              >
                <DirectionsIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {routeHistory && mqttConfig?.geolocation && mqttConfig.history && (
          <RouteDurationSlider
            {...routeHistory}
            geolocationList={mqttConfig.geolocation}
            pickDayHandler={() => {}}
          />
        )}
        {rightBar}
      </div>
      {
        <div
          style={{
            height: "calc(100vh - 140px)",
            width: "calc(100% - 20px)",
            margin: "0 10px",
          }}
        >
          <AzureMap
            options={option as IAzureMapOptions}
            controls={controls}
            trafficOptions={readyForUser ? trafficOptions : undefined}
            styleOptions={readyForUser ? styleOptions : undefined}
            cameraOptions={{ minZoom: 0, maxZoom: 24 }}
            events={{
              ready: () => {
                setReadyForUser(true);
              },
            }}
          >
            <AzureMapDataSourceProvider id="zones-source">
              <AzureMapLayerProvider
                id="zones-fill-layer"
                type="PolygonLayer"
                options={{
                  fillOpacity: 0.5,
                  fillColor: ["get", "color"],
                  strokeWidth: 0,
                }}
              />
              <AzureMapLayerProvider
                id="zones-border-layer"
                type="LineLayer"
                options={{
                  strokeColor: "black",
                  strokeWidth: 3,
                  strokeOpacity: 1,
                }}
              />
              {markers.zones}
            </AzureMapDataSourceProvider>

            <AzureMapDataSourceProvider
              id={`SinglePoint AzureMapDataSourceProvider Circle`}
            >
              <AzureMapLayerProvider
                id={`SinglePoint AzureMapLayerProvider Circle`}
                options={{
                  fillOpacity: 0.45,
                  fillColor: "#aed7f5",
                  iconOptions: {},
                }}
                events={{
                  mousemove: (e: any) => {
                    if (e.shapes && e.shapes.length > 0) {
                      const prop = e.shapes[0];
                      const coords = prop.data.geometry.coordinates;
                      if (coords && coords.length >= 2 && isValidCoordinate(coords[0]) && isValidCoordinate(coords[1])) {
                        setPopupOptions({
                          position: new data.Position(coords[0], coords[1]),
                          pixelOffset: [0, -18],
                        });
                      }
                    }
                  },
                }}
                type="PolygonLayer"
              />
              {markers.circleArea}
            </AzureMapDataSourceProvider>

            <AzureMapDataSourceProvider
              id={`MultiplePoint AzureMapDataSourceProvider Vehicle`}
            >
              <AzureMapLayerProvider
                id={`MultiplePoint AzureMapLayerProvider Vehicle`}
                options={{
                  iconOptions: {
                    image: "pin-blue",
                  },
                }}
                events={{
                  mousemove: (e: any) => {
                    if (e.shapes && e.shapes.length > 0) {
                      const prop = e.shapes[0];
                      const coords = prop.data.geometry.coordinates;
                      if (coords && coords.length >= 2 && isValidCoordinate(coords[0]) && isValidCoordinate(coords[1])) {
                        setPopupOptions({
                          position: new data.Position(coords[0], coords[1]),
                          pixelOffset: [0, -18],
                        });
                      }
                    }
                  },
                }}
                type="SymbolLayer"
              />
              {markers.vehicleLocation}
            </AzureMapDataSourceProvider>

            <AzureMapPopup
              isVisible={false}
              options={popupOptions}
              popupContent={<div>{/*popup content */}</div>}
            />
          </AzureMap>
        </div>
      }
    </div>
  );
};
